/**
 * TypeScript types for agent configuration (matching backend schemas)
 */

export enum ModelProvider {
  RAIQON = 'raiqon',
  AZURE_AI_FOUNDRY = 'azure_ai_foundry',
  CUSTOM = 'custom',
}

export enum RaiqonModel {
  // Raven 2 Medium (7B parameters)
  RAVEN_2_MEDIUM = 'raven_2_medium',

  // Raven 3 Small (4B parameters)
  RAVEN_3_SMALL = 'raven_3_small',

  // Raven 3 Medium (8B parameters)
  RAVEN_3_MEDIUM = 'raven_3_medium',

  // Raven 3 Large (32B parameters)
  RAVEN_3_LARGE = 'raven_3_large',

  // Raven 4 Medium (8B parameters, advanced reasoning)
  RAVEN_4_MEDIUM = 'raven_4_medium',

  // Raven Translator (9B parameters, multilingual)
  RAVEN_TRANSLATOR_1_MEDIUM = 'raven_translator_1_medium',

  // Raven Vision (7B parameters, multimodal)
  RAVEN_VISION_1_MEDIUM = 'raven_vision_1_medium',
}

export enum AzureAIFoundryModel {
  // OpenAI GPT
  GPT_5_PREVIEW = 'gpt-5-preview',
  GPT_5_TURBO = 'gpt-5-turbo',
  GPT_4O = 'gpt-4o',
  GPT_4O_MINI = 'gpt-4o-mini',
  GPT_4_TURBO = 'gpt-4-turbo',

  // Anthropic Claude
  CLAUDE_OPUS_4_8 = 'claude-opus-4-8',
  CLAUDE_OPUS_4_7 = 'claude-opus-4-7',
  CLAUDE_SONNET_4_6 = 'claude-sonnet-4-6',
  CLAUDE_SONNET_3_5 = 'claude-3-5-sonnet-20241022',

  // Mistral
  MISTRAL_LARGE = 'mistral-large',
  MISTRAL_NEMO = 'mistral-nemo',
  MISTRAL_SMALL = 'mistral-small',

  // Meta Llama
  LLAMA_3_1_405B = 'llama-3.1-405b-instruct',
  LLAMA_3_1_70B = 'llama-3.1-70b-instruct',
  LLAMA_3_1_8B = 'llama-3.1-8b-instruct',
}

export enum ContextFieldType {
  TEXT = 'text',
  JSON = 'json',
  LIST = 'list',
  REQUIREMENT = 'requirement',
  ANALYSIS_RESULT = 'analysis_result',
  CUSTOM = 'custom',
}

export interface ContextField {
  name: string;
  field_type: ContextFieldType;
  description: string;
  required: boolean;
  default_value?: any;
  source?: string;
  // Several fields combined into this one variable, in render order (source is then sources[0]).
  // Fields of the same item set render item by item - see _resolve_context_field_sources.
  sources?: string[];
}

export interface PromptTemplate {
  system_prompt: string;
  user_prompt_template: string;
  variables: string[];
  examples: Array<Record<string, string>>;
}

export interface ModelConfig {
  provider: ModelProvider;
  model_name: string;
  model_slug?: string;
  temperature: number;
  max_tokens?: number;
  top_p: number;
  stop_sequences: string[];
  reasoning_enabled?: boolean;
  repetition_penalty?: number;
}

export enum OutputFormat {
  TEXT = 'text',
  JSON = 'json',
  STRUCTURED = 'structured',
}

export interface OutputSchema {
  format: OutputFormat;
  output_schema?: Record<string, any>;
  validation_rules: string[];
}

export interface AgentConfig {
  id: string;
  name: string;
  description: string;
  version: string;
  slug?: string;
  task_type?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  model: ModelConfig;
  prompt: PromptTemplate;
  context_fields: ContextField[];
  output: OutputSchema;
  timeout: number;
  retry_count: number;
  tags: string[];
  category?: string;
  is_public: boolean;
  is_default: boolean;  // Whether this is a built-in default agent
  usage_count: number;
  // Derived client-side (client.ts) from the summary endpoint's raw context_config -
  // not the actual fields themselves, those still require getAgent() for the full
  // context_fields array.
  context_field_count?: number;
}

/**
 * WorkflowAgentV1 — matches CreateAgentRequest/UpdateAgentRequest in
 * raiqon/api/routers/agents_v2.py. model_slug (not model_id) is resolved to
 * model_id server-side, and prompt/inference/context/output config live
 * nested under `config`, not as flat top-level fields.
 */
export interface WorkflowAgentV1 {
  id: string;
  name: string;
  slug: string;
  version: number;
  task_type: string;
  description: string | null;
  model_slug: string | null;
  config: {
    prompt_config: Record<string, any>;   // { system_prompt, user_prompt, variables }
    inference_params: Record<string, any>; // { temperature, max_tokens, top_p, stop_sequences }
    context_config: Record<string, any>;   // { fields: [...] }
    output_config: Record<string, any>;    // { format, schema, validation_rules }
  };
  is_default: boolean;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ModelProviderInfo {
  id: string;
  name: string;
  description: string;
}

export interface ModelInfo {
  id: string;
  name: string;
  slug: string;
  description: string;
  base_model: string;
  supports_reasoning_toggle: boolean;
  supports_repetition_penalty: boolean;
}

export interface ContextFieldTypeInfo {
  id: string;
  name: string;
  description: string;
  fields?: Array<{id: string; name: string; description: string}>;
}

export interface ContextFieldCategory {
  id: string;
  name: string;
  description: string;
  fields: Array<{id: string; name: string; description: string}>;
}

export interface OutputFormatInfo {
  id: string;
  name: string;
  description: string;
}
